import { useEffect } from 'react';

/**
 * ── A MEDIÇÃO DE UMA PÁGINA DA ÁREA: o `s.js`, a identidade e a gravação ─────
 *
 * Saiu de `PlayerDaAula.ligarMedicao` porque três páginas a pedem agora — a
 * sala, a página do curso e a vitrine são as PORTAS DE NASCIMENTO da gravação
 * de sessão (ver `gravacao-da-area.ts`, no core).
 *
 * ## O que o servidor decide, e o que esta página executa
 *
 * - `consentimentoDeGravacao` (`conceder` | `negar` | `null`) — propriedade +
 *   termos, como sempre (§73.2). Continua a vir do servidor e continua a ser a
 *   primeira perna de `gravarAqui`; o navegador já não o executa sozinho.
 * - `gravarAqui` — o filtro da ÁREA (percentual por aluno, escopo, modo). É ele
 *   que decide se a configuração do gravador chega sequer a esta página.
 *
 * ## O CONSENTIMENTO AQUI DENTRO SÃO OS TERMOS (01/10/2026)
 *
 * > *«não precisamos pedir consentimento, já que o aluno aceitou os termos de
 * > uso e política de privacidade»* — o dono
 *
 * O `s.js` desta área é pedido com `?a=1`, e o servidor responde **sem**
 * `consentRequired`, sem `consentMode` e sem `consentBanner`. A barra de
 * cookies não é desenhada, nada fica enfileirado à espera de um clique, e o
 * coletor trata o evento como consentido pelo endereço — a régua inteira está
 * em `collect.ts`, no bloco `AM2`, e é ela que vale. Isto conserta uma medição,
 * e não só um incómodo: em 01/10/2026, 61 dos 67 eventos web da comunidade
 * chegavam sem visitante, sem sessão e sem identidade porque o aluno fechava a
 * barra em vez de a aceitar — e o feed os mostrava como «anônimo».
 *
 * ## E POR QUE A GRAVAÇÃO PASSA A VIAJAR POR PEDIDO, E NÃO POR OMISSÃO
 *
 * Fora da área, quem segura o `talvezGravar()` do boot é o consentimento
 * pendente. Aqui ele deixou de existir — então o freio tem de mudar de lugar,
 * ou o gravador começaria antes de a página dizer se podia.
 *
 * O freio novo é o `&g=1`: a configuração do gravador **só sai do servidor
 * quando `gravarAqui` é verdadeiro**, e `gravarAqui` é a decisão inteira do
 * servidor (termos com divulgação, escopo, modo e sorteio por aluno). A página
 * já não desfaz nada depois do `load` — o que não é enviado não precisa de ser
 * apagado. Fail-closed por construção em vez de por sequência.
 *
 * Isto substituiu três gestos cujas premissas estão registadas no corpo de
 * `ligarMedicao`; uma delas estava morta desde 21/09/2026 e a cerca que a
 * prendia continuava verde, porque lia a palavra e não o efeito.
 *
 * Uma gravação nascida numa página segue a visita até a aba fechar (é a forma
 * da SPA): escopo e modo dizem onde ela pode nascer, não onde para.
 */
export interface MedicaoDaPagina {
  publicKey: string | null;
  collectorUrl: string;
  identificadores: { externalId: string; email: string } | null;
  consentimentoDeGravacao: 'conceder' | 'negar' | null;
  gravarAqui: boolean;
  /**
   * Os nomes que esta área NÃO registra — a régua de `eventos-da-area.ts`, já
   * resolvida pelo servidor. Ausente é «registra tudo», que é o que uma aba
   * aberta antes desta obra vê.
   */
  eventosBloqueados?: string[];
}

/**
 * ── A PENEIRA DO `track()` (20/09/2026) ─────────────────────────────────────
 *
 * ## O problema, e por que ele não se resolve no servidor
 *
 * O `player.js` que toca a aula chama `window.vestigo.track('video_start', …)`
 * e o `s.js` manda aquilo direto para `/t/e`. Nenhuma rota nossa está no
 * caminho: a régua do servidor (`emitirFatosDoAluno`) governa o que o SERVIDOR
 * emite, e os quatro eventos de vídeo da web não passam por ela. Sem um filtro
 * aqui, desligar «Player de vídeo» no painel não apagaria uma linha do caminho
 * que produz quase todas elas.
 *
 * ## Por que embrulhar `track`, e não deixar de carregar o `player.js`
 *
 * Porque o `player.js` **é o player**: ele toca o vídeo, desenha os controles,
 * assina o HLS e reporta o progresso da aula. Não carregá-lo seria desligar a
 * aula para desligar a medição dela.
 *
 * O embrulho é instalado depois do `load` do `s.js` — que é quando
 * `vestigo.track` existe na sua forma final — e é idempotente pela marca
 * `__vsPeneira`: a segunda aula da sessão não empilha dois embrulhos (e
 * `ligarMedicao` roda uma vez por página do SPA, que são muitas).
 *
 * **Fail-closed por construção:** a lista só cresce quando o servidor a manda.
 * Um erro de rede que deixe `eventosBloqueados` indefinido devolve o
 * comportamento de antes — medir —, que é a escolha certa aqui: o dano de medir
 * demais é volume, e o de não medir é perder o fato.
 */
interface TrackComPeneira {
  (nome: string, params?: Record<string, unknown>): void;
  __vsPeneira?: { bloqueados: Set<string> };
}

function peneirar(bloqueados: string[]): void {
  if (bloqueados.length === 0) return;

  const alvo = window.vestigo;
  if (!alvo?.track) return;

  const atual = alvo.track as TrackComPeneira;

  /* Já embrulhado: a página nova só acrescenta os nomes dela. A área é uma só
     por aba, mas o SPA troca de página sem recarregar, e uma segunda camada de
     embrulho faria a pilha crescer a cada navegação. */
  if (atual.__vsPeneira) {
    for (const n of bloqueados) atual.__vsPeneira.bloqueados.add(n);
    return;
  }

  const estado = { bloqueados: new Set(bloqueados) };
  const embrulho: TrackComPeneira = (nome, params) => {
    if (estado.bloqueados.has(nome)) return;
    atual(nome, params);
  };
  embrulho.__vsPeneira = estado;
  alvo.track = embrulho;
}

/** Resolvida no `load` do `<script>` do `s.js` — uma por página. */
let pronto: Promise<void> | null = null;
function garantirSnippet(src: string): Promise<void> {
  /*
   * ── A DEDUPLICAÇÃO É PELO SCRIPT, E NÃO PELA URL INTEIRA ─────────────────
   *
   * Era `script[src="${src}"]`, e funcionava porque a URL era a MESMA em toda
   * página da área. Deixou de ser em 01/10/2026: o `&g=1` entra só nas páginas
   * que podem gravar, e numa SPA o aluno atravessa as duas num carregamento só.
   * Com a comparação exata, a segunda página não reconheceria o script da
   * primeira e injetaria um SEGUNDO `s.js` — dois boots, dois `PageView`, duas
   * sessões, e um `window.vestigo` substituído por baixo do embrulho da
   * peneira.
   *
   * O seletor por PREFIXO pergunta o que importa: «esta página já tem o
   * snippet desta propriedade?». A consequência, nomeada: **dentro de uma aba,
   * quem decide se a área grava é a PRIMEIRA página medida** — a URL do script
   * que foi buscado. É coerente com o que a casa já diz («uma gravação nascida
   * numa página segue a visita até a aba fechar») e erra para o lado de não
   * gravar, que é o lado certo deste erro.
   */
  const prefixo = src.split('&')[0];
  const existente = document.querySelector<HTMLScriptElement>(
    `script[src^="${prefixo}"]`,
  );
  if (existente && pronto) return pronto;
  if (existente) return Promise.resolve();
  pronto = new Promise<void>((resolve) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    s.addEventListener('load', () => resolve(), { once: true });
    /* Falhar ao baixar não pode prender a página: resolve e segue sem medir. */
    s.addEventListener('error', () => resolve(), { once: true });
    document.head.appendChild(s);
  });
  return pronto;
}

export function ligarMedicao(dados: MedicaoDaPagina): void {
  if (!dados.publicKey) {
    /* Área sem propriedade, ou sessão de suporte: a aula toca e não mede. */
    return;
  }

  window.vestigo = window.vestigo ?? { q: [] };
  if (!window.vestigo.identify) {
    window.vestigo.identify = (d) => {
      (window.vestigo!.q ??= []).push(['identify', d]);
    };
    window.vestigo.track = (n, p) => {
      (window.vestigo!.q ??= []).push(['track', n, p]);
    };
  }

  /*
   * A peneira entra DUAS vezes, e a primeira é esta — sobre o shim, antes de o
   * `s.js` sequer ser pedido.
   *
   * Sem ela haveria uma janela real: o `player.js` pode chamar `track()` antes
   * de o `s.js` terminar de carregar, e nesse instante o `track` ainda é o
   * enfileirador acima. O evento entraria na fila `q`, e quem a drena é o
   * `s.js` no boot — possivelmente antes de o `.then(load)` lá embaixo rodar. O
   * `video_start` sairia por baixo do filtro, e o sintoma seria o pior possível:
   * a área desligada emitindo *às vezes*, conforme a rede do aluno.
   *
   * Filtrar na ENTRADA da fila fecha a janela: o que não entra não é drenado.
   */
  const bloqueadosAgora = dados.eventosBloqueados ?? [];
  peneirar(bloqueadosAgora);

  const base = dados.collectorUrl.replace(/\/$/, '');
  /*
   * ── `AM2`/`AM3`: A URL DECLARA O LUGAR, E DECLARA A DECISÃO DA PÁGINA ─────
   *
   * `a=1` — «este `s.js` vai rodar numa página da área de membros». O servidor
   * responde sem `consentRequired`, sem `consentMode` e sem `consentBanner`: o
   * consentimento aqui dentro são os Termos de Uso e a Política de Privacidade
   * que o aluno aceitou no portão, e não uma barra de cookies. Decisão do dono
   * de 01/10/2026, e ela conserta uma medição: 61 dos 67 eventos web da
   * comunidade chegavam sem visitante porque ninguém clicava na barra.
   *
   * `g=1` — «e esta página pode gravar». Sem o consentimento a segurar o boot,
   * a configuração do gravador não pode viajar por omissão: ela viaja só quando
   * o SERVIDOR já disse que sim (`medicaoDaPagina`). É por isso que o `g` vai na
   * URL e não numa chamada depois do `load` — depois do `load` já é tarde.
   *
   * As duas chaves mudam a URI efetiva, que é o que separa as entradas de cache
   * (RFC 9111 §4) — a mesma conta que a rota do `s.js` já faz para dispensar o
   * `Vary: Host`.
   */
  const src =
    `${base}/t/s.js?k=${encodeURIComponent(dados.publicKey)}&a=1` +
    (dados.gravarAqui ? '&g=1' : '');

  /*
   * ── O QUE SOBROU DO CONSENTIMENTO AQUI DENTRO, E O QUE SAIU ──────────────
   *
   * Saíram três gestos, e os três tinham a mesma premissa — «o boot não grava
   * sem o `_vsconsent`»:
   *
   *  1. `localStorage.removeItem('_vsconsent')` antes de injetar o `s.js`.
   *     **Já não fazia nada desde 21/09/2026**: o consentimento mudou para
   *     cookie do domínio raiz (`f328c2d25`) e este `removeItem` continuou a
   *     apagar uma chave que ninguém escreve. Uma linha verde que não tocava em
   *     nada, e a cerca que a prendia lia a palavra, não o efeito.
   *  2. `consent(false)` sob o veredito `negar`. Com o `s.js` da área a chegar
   *     sem `consentRequired` (`AM2`), ele não segura evento nenhum — e escrever
   *     `_vsconsent='0'` num cookie do domínio RAIZ passaria uma recusa que o
   *     aluno não deu para qualquer outra página daquele domínio.
   *  3. Guardar e apagar `config.vview` depois do `load`, para desfazer tarde o
   *     que o servidor não devia ter mandado.
   *
   * No lugar dos três, uma regra só e mais forte: **a configuração do gravador
   * não viaja a não ser que o servidor tenha dito que esta página pode gravar**
   * (`&g=1`, montado acima a partir de `gravarAqui`). Fail-closed por
   * construção, em vez de fail-closed por sequência de eventos do navegador.
   *
   * `consentimentoDeGravacao` continua a vir do servidor e continua a ser a
   * primeira perna de `gravarAqui` (`medicaoDaPagina` só o põe verdadeiro sob
   * `conceder`) — o que mudou é que o navegador já não precisa de o executar.
   */
  const carregado = garantirSnippet(src);

  if (dados.identificadores) {
    window.vestigo.identify?.({
      email: dados.identificadores.email,
      externalId: dados.identificadores.externalId,
    });
  }

  /*
   * A peneira é instalada assim que o `s.js` termina de carregar.
   *
   * Continua necessária, e por um motivo que não mudou: o `s.js` substitui a
   * função da janela ao carregar, e com ela vai embora o embrulho do shim
   * (levando a marca `__vsPeneira` junto, que é o que faz este embrulho novo ser
   * instalado em vez de ignorado). Esta é a SEGUNDA instalação, sobre o `track`
   * de verdade.
   *
   * O que mudou é a urgência: dentro da área o `s.js` já não enfileira (não há
   * consentimento a esperar), então o que a primeira peneira — a do shim, lá em
   * cima — barra, nunca chega a existir.
   */
  void carregado.then(() => peneirar(bloqueadosAgora));
}

/** Liga a medição de uma página quando os dados dela chegam. */
export function useMedicaoDaPagina(dados: MedicaoDaPagina | null | undefined): void {
  useEffect(() => {
    if (dados) ligarMedicao(dados);
  }, [dados]);
}
